import React from 'react';
import { App, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const LoanFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();
  const now = dayjs();

  const save = async () => {
    try {
      const v = await form.validateFields();
      const created = await hrmsService.createLoan(v);
      message.success('Loan created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.loans() });
      navigate(`/hr/loans/${created?.id || ''}`);
    } catch {
      message.error('Create failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="New loan"
          subtitle="Create employee loan with installment schedule"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/loans')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          loan_type: 'Personal',
          start_period_year: now.year(),
          start_period_month: now.month() + 1,
          installments_total: 12,
        }}
      >
        <Card title="Loan details">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="loan_type" label="Type">
                <Select
                  options={['Personal', 'SalaryAdvance', 'Housing', 'Other'].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="principal_amount" label="Principal" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="installments_total" label="Installments" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="installment_amount" label="Installment amount (optional override)">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="start_period_year" label="Start year" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="start_period_month" label="Start month" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} max={12} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default LoanFormPage;
